import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Loader2, Eye, Trophy, MessageSquare, Calendar, ArrowRight } from 'lucide-react';
import { fetchSessionsByStatus, submitSession, deleteSession } from '@/lib/api/simulation';
import { SessionCard } from '@/components/sessions/SessionCard';
import { useCoachingSessions } from '@/hooks/useCoachingSessions';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getCoachingSessionSubtitle, getCoachingSessionTitle } from '@/lib/coachingSessionPresentation';

export default function MySessions() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('recent');
  const [practiceSessions, setPracticeSessions] = useState<any[]>([]);
  const [submittedSessions, setSubmittedSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const { data: coachingSessions = [], isLoading: coachingLoading } = useCoachingSessions();

  const recentSessions = useMemo(() => {
    const simulationItems = [
      ...practiceSessions.map((session) => ({
        id: session.id,
        type: 'simulation' as const,
        label: 'Practice',
        title: session.simulation_agents?.name || session.agent_name || 'Simulation Session',
        subtitle: session.simulation_agents?.type || 'Simulation practice',
        date: session.evaluated_at || session.created_at,
        score: typeof session.overall_score === 'number' ? session.overall_score : null,
        href: `/simulation/results/${session.id}`,
      })),
      ...submittedSessions.map((session) => ({
        id: session.id,
        type: 'simulation' as const,
        label: 'Submitted',
        title: session.simulation_agents?.name || session.agent_name || 'Simulation Session',
        subtitle: session.simulation_agents?.type || 'Submitted for review',
        date: session.submitted_at || session.evaluated_at || session.created_at,
        score: typeof session.overall_score === 'number' ? session.overall_score : null,
        href: `/simulation/results/${session.id}`,
      })),
    ];

    const coachingItems = coachingSessions.map((session) => ({
      id: session.id,
      type: 'coaching' as const,
      label: session.status.replace('_', ' '),
      title: getCoachingSessionTitle(session),
      subtitle: getCoachingSessionSubtitle(session),
      date: session.session_start || session.created_at,
      score: typeof session.readiness_score === 'number' ? session.readiness_score : null,
      href: `/coaching/results/${session.session_id}`,
    }));

    return [...simulationItems, ...coachingItems]
      .filter((session) => Boolean(session.date))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [coachingSessions, practiceSessions, submittedSessions]);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const [practice, submitted] = await Promise.all([
        fetchSessionsByStatus('practice'),
        fetchSessionsByStatus('submitted')
      ]);
      setPracticeSessions(practice);
      setSubmittedSessions(submitted);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (sessionId: string) => {
    setSubmitting(sessionId);
    const result = await submitSession(sessionId);
    setSubmitting(null);
    
    if (result.success) {
      loadSessions();
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    const result = await deleteSession(deleteConfirm);
    setDeleteConfirm(null);
    
    if (result.success) {
      loadSessions();
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">My Sessions</h1>
        <p className="text-muted-foreground mt-2">
          Review your simulation practice, submitted performances, and coaching history
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="recent" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Recent Sessions
            {recentSessions.length > 0 && (
              <Badge variant="secondary">{recentSessions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="practice" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Practice Sessions
            {practiceSessions.length > 0 && (
              <Badge variant="secondary">{practiceSessions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="submitted" className="flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Manager Review
            {submittedSessions.length > 0 && (
              <Badge variant="secondary">{submittedSessions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="coaching" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Coaching Sessions
            {coachingSessions.length > 0 && (
              <Badge variant="secondary">{coachingSessions.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Recent Sessions Tab */}
        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle>Recent Sessions</CardTitle>
              <CardDescription>
                Your latest simulation and coaching activity, newest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading || coachingLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : recentSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-muted" />
                  <p>No sessions yet</p>
                  <p className="text-sm mt-2">Start a simulation or coaching session to see it here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentSessions.map((session) => (
                    <div
                      key={`${session.type}-${session.id}`}
                      className="flex items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold ${
                          session.type === 'coaching' ? 'bg-primary' :
                          session.score !== null && session.score >= 80 ? 'bg-success' :
                          session.score !== null && session.score >= 60 ? 'bg-warning' : 'bg-destructive'
                        }`}>
                          {session.score !== null ? `${session.score}%` : <MessageSquare className="w-5 h-5" />}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-medium text-foreground truncate">{session.title}</h4>
                            <Badge variant={session.type === 'coaching' ? 'default' : 'secondary'}>
                              {session.type === 'coaching' ? 'Coach' : session.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{session.subtitle}</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {format(new Date(session.date), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(session.href)}
                      >
                        View Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Practice Sessions Tab */}
        <TabsContent value="practice">
          <Card>
            <CardHeader>
              <CardTitle>Practice Sessions</CardTitle>
              <CardDescription>
                These sessions are private - only you can see them. 
                Submit your best call when you're ready for manager feedback.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : practiceSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Eye className="w-12 h-12 mx-auto mb-4 text-muted" />
                  <p>No practice sessions yet</p>
                  <p className="text-sm mt-2">Start a simulation to practice!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {practiceSessions.map((session) => (
                    <SessionCard 
                      key={session.id}
                      session={session}
                      isPractice={true}
                      onSubmit={() => handleSubmit(session.id)}
                      onDelete={() => setDeleteConfirm(session.id)}
                      onView={() => navigate(`/simulation/results/${session.id}`)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submitted Sessions Tab */}
        <TabsContent value="submitted">
          <Card>
            <CardHeader>
              <CardTitle>Submitted for Manager Review</CardTitle>
              <CardDescription>
                These sessions are visible to your manager or team administrator for feedback.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : submittedSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="w-12 h-12 mx-auto mb-4 text-muted" />
                  <p>No submitted sessions yet</p>
                  <p className="text-sm mt-2">Practice first, then submit your best call for feedback.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {submittedSessions.map((session) => (
                    <SessionCard 
                      key={session.id}
                      session={session}
                      isPractice={false}
                      onView={() => navigate(`/simulation/results/${session.id}`)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coaching Sessions Tab */}
        <TabsContent value="coaching">
          <Card>
            <CardHeader>
              <CardTitle>Coaching Sessions</CardTitle>
              <CardDescription>
                Your coach sessions and generated documents, newest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {coachingLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : coachingSessions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted" />
                  <p>No coaching sessions yet</p>
                  <p className="text-sm mt-2">Start a coach simulation to see it here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {coachingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="flex items-center justify-between gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground truncate">
                            {getCoachingSessionTitle(session)}
                          </h4>
                          <Badge variant={
                            session.status === 'completed' ? 'default' :
                            session.status === 'failed' ? 'destructive' : 'secondary'
                          }>
                            {session.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {getCoachingSessionSubtitle(session)}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(session.session_start || session.created_at), 'MMM d, yyyy')}
                          </span>
                          {typeof session.readiness_score === 'number' && (
                            <span>Readiness Score: <span className="font-medium text-primary">{session.readiness_score}%</span></span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/coaching/results/${session.session_id}`)}
                      >
                        View Details
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Practice Session?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this practice session. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
